import { LoadingOutlined } from '@ant-design/icons';
import { Button, Col, Form, message, Popconfirm, Row, Steps, Typography } from 'antd';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import { COL_2, COL_3, NOTE_TYPES, SaveBar, Section } from './fields';
import { AsidePanel, GuidePanel, GuideThumb, noteLocks, renderField, renderGroup } from './noteBoxes';
import { DASHBOARD_SECTIONS } from './dashboardMap';
import { isIndividual, noteColumns, stepFields, stepsFor } from './noteSteps';

const { Text } = Typography;

// THE note form, one component for both surfaces (TL, 2026-09-10): Add Property creates with it and the
// note detail edits with it, so the two screens have the same organisation, the same steps, the same
// captures and the same labels — only the confirm button and what hangs around it differ. What it does
// NOT own is everything that is not a form: the note's header — with the two operator switches inline —
// and the System tab stay with the detail page (PropertyDetails), on either side of this one. Nothing
// rides between the last step and the Save bar any more (TL, 2026-09-12).
//
// Which steps it walks is `stepsFor(noteType, mode)` (noteSteps.js): the Individual Note gets the five
// sections of the investor's fact sheet with their real screenshots, every product the system mints gets
// the REDUCED set — those products have no per-note fact sheet, so the property boxes and the captures
// would be describing a screen that does not exist.
//
// Navigation is FREE and permanent (TL, 2026-09-10): any step, in any order, in both modes, by the
// header or by Back/Next, and nothing validates on the way. WHO may submit depends on the mode (TL,
// 2026-09-12): CREATING, only the confirm button on the Review — a note is minted once, at the end;
// EDITING, the Save bar carries "Save changes" on EVERY step and saves the whole form from wherever the
// operator stands — the steps are navigation, not stages, so a document goes up on step 5 without walking
// to the end. The payload is the same either way: while walking, every step is MOUNTED (the ones off
// screen are hidden, not unmounted), so the Form holds every value. Enter never submits.
//
// THE BOTTOM BAR NAMES WHAT IT DOES (TL, 2026-09-12). "Back" used to sit there next to Next/Save and read
// like the way out of the screen — leaving is the header's discreet link now (NoteHeader), and this bar
// only walks steps: "Previous step" / "Next step", both grey. Nothing here starts a process: while
// editing, the ONE primary button is "Save changes", so an operator can walk a Constitution note end to
// end knowing only the Save writes. And "Reset" — a name that said nothing about what it threw away — is
// "Discard changes": it appears ONLY once a box has been touched, asks first, and puts the form back to
// the values the note is stored with.

export { noteColumns, stepsFor };

// One Card per step with ALL its boxes, three per row: the same Field definitions the steps render, on
// the one Form — a change here IS the value that gets saved, nothing to copy back.
function renderReviewStep(item, ctx) {
  return (
    <Section
      key={item.key}
      title={
        item.dashboard ? (
          <span>
            {item.title}{' '}
            <Text type="secondary" style={{ fontWeight: 'normal' }}>
              — investor dashboard, {DASHBOARD_SECTIONS[item.dashboard]}
            </Text>
          </span>
        ) : (
          item.title
        )
      }
      hint={item.guide ? <GuideThumb step={item} /> : null}
    >
      {item.groups.map((group, index) => (
        <React.Fragment key={group.title || index}>
          {group.title && (
            <Col xs={24}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                {group.title}
              </Text>
            </Col>
          )}
          {group.fields.map((name) => renderField(name, ctx))}
        </React.Fragment>
      ))}
    </Section>
  );
}

export function NoteWizard({
  mode = 'create',
  form,
  investment,
  initialValues,
  propTags = [],
  loanTags = [],
  onFinish,
  loading = false,
  submitLabel = 'Save changes',
  reviewIntro,
  beforeSubmit = null,
}) {
  const [current, setCurrent] = useState(0);
  // Only what the operator typed makes the Discard offer appear. `form.isFieldsTouched()` would stay true
  // after a Save (antd keeps the touched marks), so the wizard tracks it and clears it when the form is
  // handed over.
  const [dirty, setDirty] = useState(false);

  const noteType = mode === 'create' ? NOTE_TYPES.individual : investment?.noteType;
  const steps = stepsFor(noteType, mode);
  const review = steps.length - 1;
  const reduced = !isIndividual(noteType) && mode !== 'create';
  // Editing saves from any step; creating only from the Review.
  const savesAnywhere = mode === 'edit';
  const locks = noteLocks(investment, mode);
  const ctx = { mode, investment, locks, propTags, loanTags, reduced, span: COL_2 };

  // Free navigation (TL, 2026-09-10): every step is reachable from every step, in both modes, with
  // nothing to validate on the way. Only the confirm button submits.
  const goTo = (step) => {
    if (step < 0 || step > review) return;
    setCurrent(step);
    const scroller = document.querySelector('section.ant-layout');
    if (scroller) scroller.scrollTo({ top: 0 });
  };

  const onFinishFailed = ({ errorFields }) => {
    if (isEmpty(errorFields)) return;
    message.error(errorFields.map((field) => field.errors[0]).join(' · '));
    // Saving away from the Review can fail on a box that is mounted but hidden: walk to the step that
    // owns the first error so the operator sees it in its place (on the Review every box is on screen).
    if (current === review) return;
    const failed = errorFields[0].name[0];
    const owner = steps.findIndex((item) => stepFields(item).includes(failed));
    if (owner >= 0 && owner !== current) goTo(owner);
  };

  const submit = async (values) => {
    // Belt and braces for the create: its confirm button lives only on the Review, and a stray Enter on
    // a <select> must never mint a note from step 2.
    if (!savesAnywhere && current !== review) return;
    await onFinish(values);
    // Only while EDITING: creating leaves for the new note's detail, and touching state after that is a
    // React update on an unmounted component.
    if (savesAnywhere) setDirty(false);
  };

  // Back to what the note is STORED with — `resetFields` alone would rewind to the values the form was
  // mounted with, which is a different thing after a save.
  const discard = () => {
    form.resetFields();
    if (initialValues) form.setFieldsValue(initialValues);
    setDirty(false);
  };

  const step = steps[current];
  const isReview = current === review;

  // Beside the screenshot (or the aside) the fields sit two per row, mirroring the section's two columns.
  const renderStep = (item) => {
    const panel = item.guide ? <GuidePanel step={item} /> : <AsidePanel step={item} noteType={noteType} />;
    if (!panel) {
      return <Row gutter={24}>{item.groups.map((group, index) => renderGroup(group, index, { ...ctx, span: COL_3 }))}</Row>;
    }
    return (
      <Row gutter={24}>
        <Col xs={24} xl={13}>
          {item.groups.map((group, index) => renderGroup(group, index, ctx))}
        </Col>
        <Col xs={24} xl={11}>
          {panel}
        </Col>
      </Row>
    );
  };

  return (
    <>
      <Steps size="small" current={current} onChange={goTo} style={{ marginBottom: 24 }}>
        {steps.map((item) => (
          <Steps.Step key={item.key} title={item.title} description={item.caption || (item.dashboard ? 'Dashboard' : 'Note')} />
        ))}
      </Steps>

      <Form
        form={form}
        layout="vertical"
        // Every box carries one line under its label (noteBoxes § BoxLabel) and antd clips a multi-line
        // label without this.
        labelWrap
        initialValues={initialValues}
        onValuesChange={() => setDirty(true)}
        onFinish={submit}
        onFinishFailed={onFinishFailed}
        name={mode === 'create' ? 'create-investment' : 'update-investment'}
        // Enter in a box never submits (the Review is editable too): only the confirm button does.
        onKeyDown={(event) => {
          if (event.key === 'Enter' && event.target.tagName === 'INPUT') event.preventDefault();
        }}
      >
        {/* A Field is mounted in ONE place at a time — on its step while walking, on the Review with
            every other box once there — so no name is registered twice; the Form keeps the values
            across the move (preserve). */}
        {/* A box wears the investor's word only where there is a fact sheet to borrow it from: `reduced`
            travels in the ctx and noteBoxes decides that per box. */}
        {!isReview &&
          steps.slice(0, review).map((item, index) => (
            <div key={item.key} style={{ display: index === current ? 'block' : 'none' }}>
              {renderStep(item)}
            </div>
          ))}
        {isReview && (
          <>
            {reviewIntro && (
              <p style={{ marginBottom: 16 }}>
                <Text type="secondary">{reviewIntro}</Text>
              </p>
            )}
            {steps.slice(0, review).map((item) => renderReviewStep(item, { ...ctx, span: COL_3 }))}
          </>
        )}

        {isReview && beforeSubmit}

        <SaveBar>
          <Button htmlType="button" onClick={() => goTo(current - 1)} disabled={current === 0 || loading}>
            Previous step
          </Button>
          {!isReview && (
            // While editing the prominent button is the Save: the step buttons only move, and they say so.
            <Button type={savesAnywhere ? 'default' : 'primary'} htmlType="button" onClick={() => goTo(current + 1)}>
              Next step
            </Button>
          )}
          {(isReview || savesAnywhere) && (
            <Button type="primary" htmlType="submit" disabled={loading}>
              {loading ? <LoadingOutlined /> : submitLabel}
            </Button>
          )}
          {savesAnywhere && dirty && (
            <Popconfirm
              title="Discard your unsaved changes and go back to the saved values?"
              okText="Discard changes"
              okButtonProps={{ danger: true }}
              cancelText="Keep editing"
              onConfirm={discard}
              disabled={loading}
            >
              <Button type="default" htmlType="button" disabled={loading}>
                Discard changes
              </Button>
            </Popconfirm>
          )}
          <Text type="secondary" style={{ marginLeft: 'auto' }}>
            Step {current + 1} of {steps.length} · {step.title}
          </Text>
        </SaveBar>
      </Form>
    </>
  );
}
